import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { FilterSection } from '../../../components/common/filter/FilterSection/FilterSection';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { IMarketPriceFilter } from '../../../constants/filter/marketPriceFilter.constant';
import { marketPricePageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetMachinePriceStatistics } from '../../../hooks/api/marketPrice/queries/useGetMachinePriceStatistics.query';
import { useAddFilterOptions } from '../../../hooks/filter/useAddFilterOptions';
import { useCheckSearchParamUpdate } from '../../../hooks/filter/useCheckSearchParamUpdate';
import { useUpdateMarketPriceFilterStates } from '../../../hooks/filter/useUpdateMarketPriceFilterStates';
import { useUpdateMarketPriceListPageSearchParam } from '../../../hooks/filter/useUpdateMarketPriceListPageSearchParam';
import { useGetDownloadAppModalHeight } from '../../../hooks/modal/useGetDownloadAppModalHeight';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useInfiniteScrollLoadingTarget } from '../../../hooks/scroll/useInfiniteScrollLoadingTarget';
import { useAppDispatch, useAppSelector } from '../../../hooks/store/useStore';
import { IMarketPrice } from '../../../interfaces/MarketPrice.interfaces';
import { filterSlice } from '../../../store/filterSlice';
import { createMarketPriceFilterSearchParams } from '../../../utils/filter/createMarketPriceFilterSearchParams.util';
import { TractorValueDetailCard } from '../../../views/Product/listView/TractorValueDetailCard/TractorValueDetailCard';

export const MarketPriceListPage = () => {
  const dispatch = useAppDispatch();
  const filterActions = filterSlice.actions;

  useAddFilterOptions({ filterType: 'marketPriceFilter', data: 'brands', applicableMachineType: 'tractor' });
  useCheckSearchParamUpdate({ filterType: 'marketPriceFilter' });
  useUpdateMarketPriceFilterStates();
  useUpdateMarketPriceListPageSearchParam();

  const fixedHeaderHeight = 48;
  const downloadAppModalHeight = useGetDownloadAppModalHeight();
  const marketPriceFilterStates = useAppSelector((state) => state.filter.marketPriceFilter);

  useEffect(() => {
    if (marketPriceFilterStates.globalFilters.length > 0 && !marketPriceFilterStates.selectedFilterKey) {
      const hasNonDefaultFilters = marketPriceFilterStates.globalFilters.some((filter) => {
        if (filter.optionType === 'radio' || filter.optionType === 'checkbox') {
          return filter.options.some((option) => option.isSelected && !option.isDefault);
        }
        if (filter.optionType === 'slider') {
          return filter.options.some((option) => option.value !== option.defaultValue);
        }
        return false;
      });

      if (!hasNonDefaultFilters) {
        dispatch(
          filterActions.setSelectedFilterKey({
            filterType: 'marketPriceFilter',
            value: 'filter_brand',
          }),
        );
        dispatch(
          filterActions.setIsAutoOpened({
            filterType: 'marketPriceFilter',
            value: true,
          }),
        );
      }
    }
  }, []);

  const {
    data: machinePriceList,
    isLoading: isGetMachinePriceStatisticsQueryLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetMachinePriceStatistics({
    searchParam: {
      ...createMarketPriceFilterSearchParams({
        filters: marketPriceFilterStates.globalFilters as IMarketPriceFilter[],
      }),
      count: 5,
    },
  });

  const { loadingTargetRef, isLoadingTargetRendered } = useInfiniteScrollLoadingTarget({
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    triggeredFnWhenInView: fetchNextPage,
  });

  useLayout({
    fixedHeader: (
      <MainHead backButton={{ isShow: false }} headerTitle={'트랙터 시세'} hasSearchButton hasMyInfoButton />
    ),
    fixedHeaderHeight,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
    style: { background: '#fff' },
  });

  if (isGetMachinePriceStatisticsQueryLoading) {
    return <AnimateLoading />;
  }

  if (machinePriceList) {
    return (
      <>
        <Helmet>
          <title>{marketPricePageInfo.listPage.title}</title>
        </Helmet>
        <MarketPriceListWrapper>
          <>
            <FilterSection
              filterType={'marketPriceFilter'}
              style={{
                position: 'sticky',
                top: `${fixedHeaderHeight + downloadAppModalHeight}px `,
                backgroundColor: 'white',
                zIndex: 55,
              }}
            />
            <MarketPriceCardGroup>
              {machinePriceList.pages.map((page) =>
                page.map((machinePrice: IMarketPrice) => (
                  <TractorValueDetailCard key={machinePrice.machine.id} product={machinePrice} />
                )),
              )}
              {isLoadingTargetRendered && <LoadingTarget ref={loadingTargetRef} />}
            </MarketPriceCardGroup>
          </>
        </MarketPriceListWrapper>
      </>
    );
  }

  return null;
};

const MarketPriceListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const MarketPriceCardGroup = styled.div`
  display: flex;
  padding: 16px 16px 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
`;

const LoadingTarget = styled.div``;
